import { Link } from "react-router-dom";
import { Phone, CheckCircle, MapPin, Sparkles, Heart, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { contactInfo, pricing } from "../data";
import CoverageMap from "../components/CoverageMap";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.1
    }
  },
  viewport: { once: true }
};

export default function Home() {
  const featuredPrice = pricing[0].items[0];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 lg:pt-56 lg:pb-40 flex items-center justify-center text-center min-h-[92vh]">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero-closeup.png"
            alt="Professionel fodpleje"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/65 backdrop-blur-[1px]"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex flex-wrap items-center justify-center gap-2 mb-6"
          >
            <span className="bg-white/15 backdrop-blur-md px-3.5 py-1 rounded-full text-xs sm:text-sm font-medium text-white border border-white/20 flex items-center gap-1.5 shadow-sm">
              <i className="fa-solid fa-location-dot text-brand-light" />
              <span>Kruså / Padborg & omegn</span>
            </span>
            <span className="text-white/40 hidden sm:inline">•</span>
            <span className="bg-white/15 backdrop-blur-md px-3.5 py-1 rounded-full text-xs sm:text-sm font-medium text-white border border-white/20 flex items-center gap-1.5 shadow-sm">
              <i className="fa-solid fa-location-dot text-brand-light" />
              <span>Faaborg / Millinge & omegn</span>
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-tight mb-6"
          >
            Professionel fodpleje <br />
            <span className="font-display text-white block mt-1">i trygge rammer</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-stone-200 text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl"
          >
            Både udekørende og med faste klinikker. Som lægeexamineret fodplejer tilbyder jeg professionel behandling i mine klinikker i Kollund og Dyreborg eller som udekørende hjemmebesøg i Kruså/Padborg & omegn samt Faaborg/Millinge & omegn.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 w-full sm:w-auto"
          >
            <a
              href={`tel:${contactInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white bg-transparent px-8 py-4 text-base font-medium text-white shadow-lg hover:bg-white hover:text-brand-dark transition-all hover:scale-102 active:scale-98"
            >
              <Phone className="h-4 w-4" />
              Ring og book tid
            </a>
            <Link
              to="/behandlinger/fodbehandling"
              className="inline-flex items-center justify-center gap-1 rounded-full bg-white/10 backdrop-blur-md px-8 py-4 text-base font-medium text-white shadow-xs hover:bg-white/20 transition-all border border-white/20"
            >
              Se behandlinger & priser
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Focus Boxes: Behandlinger */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            {...fadeIn}
            className="text-center mb-16"
          >
            <h2 className="text-3xl tracking-tight text-stone-900 sm:text-4xl mt-2">Mine mest populære behandlinger</h2>
            <p className="mt-4 text-lg text-stone-600 max-w-2xl mx-auto">Professionel pleje skræddersyet til dine fødders behov – altid med fokus på sundhed og velvære.</p>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-10"
          >
            {[
              {
                title: "Klinikbehandling",
                price: "450,-",
                description: "Nyd en behagelig behandling i rolige omgivelser i min klinik i enten Kollund (Kruså/Padborg) eller Dyreborg (Faaborg/Millinge).",
                image: "/images/fodbehandling-basic.png",
                badge: "I Klinik",
                link: "/behandlinger/fodbehandling",
              },
              {
                title: "Udekørende Fodpleje",
                price: "450,-",
                description: "Få professionel fodbehandling hjemme hos dig selv. Jeg kører ud i Kruså/Padborg & omegn samt Faaborg/Millinge & omegn.",
                image: "/images/fodbehandling-mobile.png",
                badge: "Hjemmebesøg",
                link: "/behandlinger/udebehandling",
              },
              {
                title: "Udvidet Fodbehandling",
                price: "550,-",
                description: "Til fødder med særlige udfordringer – tilbydes både i klinikken og som udekørende behandling.",
                image: "/images/fodbehandling-luxury.png",
                link: "/behandlinger/fodbehandling",
              },
            ].map((item, idx) => (
              <motion.div key={idx} variants={fadeIn} className="h-full">
                <Link 
                  to={item.link}
                  className="group flex flex-col bg-stone-50/40 rounded-3xl overflow-hidden border border-stone-100 shadow-xs hover:shadow-xl hover:bg-white transition-all duration-300 h-full relative"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.title} 
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Price Badge */}
                    <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-stone-200/40 shadow-sm text-sm font-bold text-stone-900">
                      {item.price}
                    </div>

                    {/* Optional Highlight Badge */}
                    {item.badge && (
                      <div className="absolute top-4 left-4 bg-orange-900 text-brand-cream px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase">
                        {item.badge}
                      </div>
                    )}
                  </div>
                  <div className="p-8 flex flex-col flex-grow">
                    <h3 className="text-xl text-stone-900 mb-3 group-hover:text-orange-900 transition-colors font-medium">{item.title}</h3>
                    <p className="text-stone-600 mb-6 flex-grow leading-relaxed text-sm">
                      {item.description}
                    </p>
                    <div 
                      className="inline-flex items-center text-stone-900 font-semibold group-hover:text-orange-900 transition-colors text-sm"
                    >
                      Læs mere og se priser
                      <Sparkles className="ml-2 h-4 w-4 text-orange-700" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Geografisk dækning - Interaktivt kort (Green Background) */}
      <section className="py-20 bg-[#1e3027] border-y border-emerald-950/20 text-stone-100">
        <CoverageMap className="!py-0 !bg-transparent !border-none" />
      </section>

      {/* Trust & Service Section (Light and Clean) */}
      <section className="py-20 bg-white border-y border-stone-100 text-stone-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            {...fadeIn}
            className="text-center mb-16"
          >
            <h2 className="text-3xl tracking-tight text-stone-900 sm:text-4xl mt-2 font-light">Hvorfor vælge Juhler Fodpleje?</h2>
            <p className="mt-4 text-lg text-stone-600 max-w-2xl mx-auto">Jeg lægger stor vægt på den gode oplevelse, den faglige stolthed og det personlige møde.</p>
          </motion.div>
          <motion.div 
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full"
          >
            {[
              {
                title: "Lægeexamineret fodplejer",
                description: "Din garanti for en professionel behandling baseret på solid faglig viden, høj hygiejne og de nyeste teknikker inden for fodpleje.",
                icon: CheckCircle,
              },
              {
                title: "Både klinik & udekørende",
                description: "Vælg frit mellem en behagelig behandling i klinikken i Kollund/Dyreborg eller et udekørende hjemmebesøg i Kruså/Padborg & omegn og Faaborg/Millinge & omegn.",
                icon: MapPin,
              },
              {
                title: "Personlig service",
                description: "Her er der altid god tid til den enkelte. Jeg lægger stor vægt på nærvær, omsorg, tryghed og professionel vejledning.",
                icon: Heart,
              },
            ].map((feature, idx) => (
              <motion.div 
                key={idx} 
                variants={fadeIn}
                className="bg-stone-50 p-8 rounded-2xl border border-stone-200/40 shadow-xs hover:shadow-sm transition-shadow text-stone-900"
              >
                <div className="h-12 w-12 rounded-xl bg-orange-900 flex items-center justify-center mb-6">
                  <feature.icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-medium text-stone-900 mb-3">{feature.title}</h3>
                <p className="text-stone-600 leading-relaxed text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Price Snippet & Booking Section */}
      <motion.section 
        {...fadeIn}
        className="py-24 text-stone-900 relative"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Side: Featured Treatment Card */}
            <div className="lg:col-span-7">
              <h2 className="text-3xl sm:text-4xl font-light mb-6 text-stone-900 mt-2">Enkel og gennemskuelig prisliste</h2>
              <p className="text-stone-600 text-lg mb-8 max-w-xl">
                Det skal være trygt og nemt at få professionel fodpleje. Jeg har ingen skjulte gebyrer, og du kender altid prisen på forhånd.
              </p>
              
              <div className="bg-stone-50 border border-stone-200/60 p-8 rounded-3xl shadow-xs relative overflow-hidden group hover:shadow-md transition-all duration-300">
                <div className="absolute top-0 left-0 w-2 h-full bg-orange-900" />
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4">
                  <span className="font-semibold text-xl text-stone-900">{featuredPrice.title}</span>
                  <span className="text-3xl font-bold text-orange-900 shrink-0">{featuredPrice.price}</span>
                </div>
                <p className="text-stone-600 text-sm leading-relaxed mb-6">
                  {featuredPrice.description}
                </p>
                {featuredPrice.subOption && (
                  <div className="inline-flex items-center gap-2 text-stone-700 text-xs bg-orange-50/60 px-3.5 py-2 rounded-lg border border-orange-100/50">
                    <CheckCircle className="h-3.5 w-3.5 text-orange-800" />
                    <span>{featuredPrice.subOption.title}: <strong className="text-stone-900">{featuredPrice.subOption.price}</strong></span>
                  </div>
                )}
              </div>
              
              <div className="mt-6">
                <Link
                  to="/behandlinger/fodbehandling"
                  className="inline-flex items-center gap-2 text-orange-900 hover:text-orange-700 transition-colors font-semibold text-sm"
                >
                  Se hele prislisten og alle ydelser →
                </Link>
              </div>
            </div>

            {/* Right Side: Booking CTA */}
            <div className="lg:col-span-5 bg-orange-50/40 border border-orange-100/60 rounded-[2.5rem] p-8 md:p-10 flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-2xl bg-orange-100 flex items-center justify-center mb-6">
                <Calendar className="h-7 w-7 text-orange-900" />
              </div>
              <h3 className="text-2xl font-light mb-4 text-stone-900">
                Klar til at give dine fødder nyt liv?
              </h3>
              <p className="text-stone-600 text-sm mb-8 max-w-xs">
                Bestil din næste behandling i dag. Jeg svarer hurtigt og glæder mig til at hjælpe dig.
              </p>
              
              <a
                href={`tel:${contactInfo.phone}`}
                className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-brand-dark px-8 py-4.5 text-lg font-bold text-brand-cream shadow-lg hover:bg-brand-medium transition-all hover:scale-102 active:scale-98 border border-brand-dark/20"
              >
                <Phone className="h-5 w-5 text-orange-400" />
                Ring: {contactInfo.phoneDisplay}
              </a>
              <p className="mt-4 text-xs text-stone-500 uppercase tracking-widest font-medium">Kun telefonisk booking</p>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
